import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import CloseIcon from '@mui/icons-material/Close'
import {
  Autocomplete,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import ReplacementAPI from 'api/faber/ReplacementAPI'
import RoundedFilledLoadingButton from 'common/Button/RoundedFilledLoadingButton'
import NormalText from 'common/Typography/NormalText'
import {
  Formik,
  FormikContextType,
  FormikHelpers,
  useFormikContext
} from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as yup from 'yup'
const apnOptions = ['globaldata', 'internet', 'mobile', 'kroly']
const imsiOptions = ['25', '26', '7', '2']
const initialValues = {
  apn: 'globaldata',
  imsi: '25',
  issue: [] as string[],
  country: '',
  otherIssueDescription: ''
}
const issueList = [
  'only 2g/3g',
  'receiving only LTE',
  'no 5G',
  'connection keeps dropping/intermittent connection',
  'others'
]

const validation = yup.object().shape({
  apn: yup.string().required('This is a required field'),
  imsi: yup.string().required('This is a required field'),
  issue: yup.array().min(1, 'Please select at least one issue'),
  otherIssueDescription: yup.string().when('issue', {
    is: (issue) => issue.includes('others'),
    then: yup.string().required('This is a required field')
  }),
  country: yup.string().required('This is a required field')
})

const useIssueFormik = (): FormikContextType<typeof initialValues> =>
  useFormikContext()

function SwitchWhitelistDialog({
  handleDialogClose,
  isDialogOpen,
  esimWhitelist,
  iccid,
  userId,
  isExternal
}: {
  handleDialogClose: () => void
  isDialogOpen: boolean
  esimWhitelist: string
  iccid: string
  userId: string
  isExternal: boolean
}) {
  const faberToken = OrbitSession.get('faberToken')
  const { mutate: switchWhitelist, isLoading: isSwitchWhitelistLoading } =
    useMutation(
      isExternal
        ? ReplacementAPI.externalSwitchWhitelist
        : ReplacementAPI.switchWhitelist,
      {
        onSuccess: (res) => {
          toast.success(`${res.data?.message}-Whitelist switched successfully`)
          handleDialogClose()
        },
        onError: (res: { error: string; status: string }) => {
          toast.error(
            `${res.error}- There is an error switching whitelist, please try again or contact support`
          )
        }
      }
    )

  const handleSubmitForm = (
    values: typeof initialValues,
    formikHelpers: FormikHelpers<typeof initialValues>
  ) => {
    const otherIssue = values.otherIssueDescription
    const allIssues = values.issue

    if (otherIssue !== '') {
      allIssues.push(otherIssue)
    }
    const dataToSubmit = {
      apn: values.apn,
      imsi: values.imsi,
      issue: allIssues,
      country: values.country,
      iccids: [iccid],
      userId
    }
    switchWhitelist(
      { faberToken, ...dataToSubmit },
      {
        onSuccess: () => {
          formikHelpers.resetForm()
        }
      }
    )
  }

  return (
    <Dialog
      fullWidth
      maxWidth='lg'
      onClose={handleDialogClose}
      aria-labelledby='customized-dialog-title'
      open={isDialogOpen}
    >
      <Stack direction={'row'}>
        <DialogTitle>
          Network troubleshooting steps (SWITCHING OF NETWORKS WHITELIST )
        </DialogTitle>
        <IconButton
          aria-label='close'
          onClick={handleDialogClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <DialogContent dividers sx={{ height: '60vh' }}>
        <NormalText>ESIM WHITELIST: {esimWhitelist}</NormalText>

        <Stack
          sx={{
            backgroundColor: '#fff4e5'
          }}
        >
          <NormalText isBold sx={{ textAlign: 'center' }}>
            Please ensure that appropriate troubleshooting steps have been done
            before proceeding with the following steps AND countries affected
            include any of the following: Australia, China, France, Japan,
            Malaysia, Taiwan, Thailand, United States
          </NormalText>
        </Stack>
        <Stack>
          {esimWhitelist === '7932' || esimWhitelist === '11906' ? (
            <>
              <Formik
                validationSchema={validation}
                initialValues={initialValues}
                onSubmit={handleSubmitForm}
              >
                <IssueForm
                  isSwitchWhitelistLoading={isSwitchWhitelistLoading}
                />
              </Formik>
            </>
          ) : (
            <NormalText>
              Switching of network whitelist is not applicable for this
              customer. Please escalate issue to L3 if customer faces network
              issues
            </NormalText>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export function IssueForm({
  isSwitchWhitelistLoading
}: {
  isSwitchWhitelistLoading: boolean
}) {
  const [isLoading, setIsLoading] = useState(false)
  const {
    handleChange,
    handleBlur,
    values,
    errors,
    isValid,
    setFieldValue,
    submitForm
  } = useIssueFormik()

  const [sortedLocations, setSortedLocations] = useState<string[]>([])
  const { data: whitelistLocations, isSuccess } = useQuery({
    queryKey: ['whitelistLocations'],
    queryFn: async () => {
      const faberToken = OrbitSession.get('faberToken')
      const response = await ReplacementAPI.getWhitelistLocations(faberToken)
      return response.data
    }
  })

  useEffect(() => {
    if (!isSwitchWhitelistLoading) {
      setIsLoading(false)
    }
  }, [isSwitchWhitelistLoading])

  useEffect(() => {
    if (isSuccess && whitelistLocations.data) {
      const sortedData = whitelistLocations.data.sort((a, b) =>
        a.toLowerCase().localeCompare(b.toLowerCase())
      )
      setSortedLocations(sortedData)
    }
  }, [whitelistLocations?.data, isSuccess])

  return (
    <>
      <Stack>
        <NormalText>
          Please fill in the following form with the information given by the
          customer based on the troubleshooting steps done
        </NormalText>
        <NormalText isBold>Country</NormalText>
        <Autocomplete
          onChange={(e, value) => {
            setFieldValue('country', value ? value : '')
          }}
          options={sortedLocations}
          renderInput={(params) => <TextField name='country' {...params} />}
        />
        {errors.country && (
          <NormalText sx={{ color: 'red' }}>{errors.country}</NormalText>
        )}
        <NormalText isBold>APN</NormalText>
        <RadioGroup
          aria-labelledby='apn-radio-btn'
          defaultValue='globaldata'
          name='apn'
          onChange={handleChange}
        >
          {apnOptions.map((apn) => (
            <FormControlLabel
              key={apn}
              value={apn}
              control={<Radio />}
              label={apn}
            />
          ))}
        </RadioGroup>
        {errors.apn && (
          <NormalText sx={{ color: 'red' }}>{errors.apn}</NormalText>
        )}
      </Stack>
      <Stack>
        <NormalText isBold>IMSI</NormalText>
        <RadioGroup
          aria-labelledby='imsi-radio-btn'
          defaultValue='25'
          name='imsi'
          onChange={handleChange}
        >
          {imsiOptions.map((imsi) => (
            <FormControlLabel
              key={imsi}
              value={imsi}
              control={<Radio />}
              label={imsi}
            />
          ))}
        </RadioGroup>
        {errors.imsi && (
          <NormalText sx={{ color: 'red' }}>{errors.imsi}</NormalText>
        )}
      </Stack>
      <Stack>
        <NormalText isBold>Issue</NormalText>
        <Stack pl={2} mb={1}>
          <FormGroup
            onChange={(e) => {
              handleChange(e)
            }}
          >
            {issueList.map((issue) => {
              return (
                <FormControlLabel
                  name='issue'
                  key={issue}
                  value={issue}
                  control={<Checkbox />}
                  label={issue}
                />
              )
            })}
          </FormGroup>
          {errors.issue && (
            <NormalText sx={{ color: 'red' }}>{errors.issue}</NormalText>
          )}
        </Stack>

        {values.issue.includes('others') ? (
          <>
            <TextField
              placeholder='please describe more about the issue'
              multiline
              name='otherIssueDescription'
              onChange={(e) => {
                handleBlur(e)
                handleChange(e)
              }}
            />
            {errors.otherIssueDescription && (
              <NormalText sx={{ color: 'red' }}>
                {errors.otherIssueDescription}
              </NormalText>
            )}
          </>
        ) : null}
      </Stack>
      <Stack m={2} sx={{ maxWidth: '800px', mx: 'auto' }}>
        <RoundedFilledLoadingButton
          disabled={!isValid}
          sx={{ textTransform: 'initial' }}
          onClick={() => {
            submitForm()
            setIsLoading(true)
          }}
          loading={isLoading}
        >
          Click here to submit form and switch whitelist for customers
        </RoundedFilledLoadingButton>
      </Stack>
    </>
  )
}

export default SwitchWhitelistDialog
